import React, { useState, useEffect } from 'react'
import { useEventsStore } from '../../stores/eventsStore'
import { useAuthStore } from '../../stores/authStore'
import { useFamilyStore } from '../../stores/familyStore'
import { useUserPreferencesStore } from '../../stores/userPreferencesStore'
import { getColorScheme } from '../../utils/colorSchemes'
import { getTypeColor } from '../../utils/eventTypeColors'
import { useEntitlements } from '../../hooks/useEntitlements'
import { NOTE_DURATION_OPTIONS, formatNoteDuration } from './noteDurations'

interface EventModalProps {
  isOpen: boolean
  onClose: () => void
  defaultDate: Date
  childId: string
  eventId?: string
}

export const EventModal: React.FC<EventModalProps> = ({ isOpen, onClose, defaultDate, childId, eventId }) => {
  const { addEvent, updateEvent, events } = useEventsStore()
  const { user } = useAuthStore()
  const { family, members } = useFamilyStore()
  const scheme = useUserPreferencesStore(state => getColorScheme(state.preferences.color_scheme))
  const { plan: entitlements } = useEntitlements(family?.id)

  const existing = events.find(e => e.id === eventId)

  const toLocalInput = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0,16)
  const fromLocalInput = (s: string) => new Date(s)

  const defaultNoon = (date: Date) => {
    const d = new Date(date)
    d.setHours(12, 0, 0, 0)
    return d
  }

  const [title, setTitle] = useState(existing?.title || '')
  // Nowa semantyka: 'note' = dawne 'event'; 'expense' bez zmian.
  const initialType = existing?.type === 'event' ? 'note' : (existing?.type || 'note')
  const [type, setType] = useState<'note' | 'expense'>(initialType as 'note' | 'expense')
  const [allDay, setAllDay] = useState(existing?.all_day || false)
  const [startAt, setStartAt] = useState(() => toLocalInput(existing ? new Date(existing.start_at) : defaultNoon(defaultDate)))
  const [endAt, setEndAt] = useState(() => toLocalInput(existing && existing.end_at ? new Date(existing.end_at) : new Date(defaultNoon(defaultDate).getTime() + 60*60*1000)))
  // Czas trwania notatki (w minutach). UI używa selektora zamiast ręcznego końca.
  const deriveInitialDuration = () => {
    if (existing && existing.end_at) {
      const startMs = new Date(existing.start_at).getTime()
      const endMs = new Date(existing.end_at).getTime()
      const diffMin = Math.max(0, Math.round((endMs - startMs) / 60000))
      // Dłuższe niż największa opcja zostają bez zmian (dokładana jako dodatkowa opcja), żeby edycja nie skracała notatki
      return NOTE_DURATION_OPTIONS.find(v => v >= diffMin) ?? diffMin
    }
    return 60 // domyślnie 1 godzina
  }
  const [durationMinutes, setDurationMinutes] = useState<number>(deriveInitialDuration())
  const [location, setLocation] = useState(existing?.location || '')
  const [notes, setNotes] = useState(existing?.notes || '')
  const [amount, setAmount] = useState<number | ''>(existing?.amount ?? '')
  const [currency, setCurrency] = useState(existing?.currency || 'PLN')
  const [parentsOnly, setParentsOnly] = useState(existing?.parents_only || false)
  // kategoria nieużywana – uproszczenie zgodnie z wymaganiami
  const [isSaving, setIsSaving] = useState(false)

  // Aktualizuj domyślne daty przy otwarciu modala dla nowego wydarzenia
  useEffect(() => {
    if (isOpen && !existing) {
      const start = defaultNoon(defaultDate)
      const end = new Date(start.getTime() + 60*60*1000)
      setStartAt(toLocalInput(start))
      setEndAt(toLocalInput(end))
      setDurationMinutes(60)
      // Resetuj pola tekstowe dla nowego wpisu – zaczynamy od czystego formularza
      setTitle('')
      setLocation('')
      setNotes('')
      setAmount('')
      setCurrency('PLN')
      setAllDay(false)
      setParentsOnly(false)
      // Opcjonalnie można też przywrócić domyślny typ 'event'
      // setType('event')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, defaultDate, eventId])

  useEffect(() => {
    if (existing) {
      setTitle(existing.title)
      setType((existing.type === 'event' ? 'note' : existing.type) as any)
      setAllDay(existing.all_day)
      setStartAt(toLocalInput(new Date(existing.start_at)))
      setEndAt(existing.end_at ? toLocalInput(new Date(existing.end_at)) : toLocalInput(new Date(defaultNoon(defaultDate).getTime() + 60*60*1000)))
      setDurationMinutes(deriveInitialDuration())
      setLocation(existing.location || '')
      setNotes(existing.notes || '')
      setAmount(existing.amount ?? '')
      setCurrency(existing.currency || 'PLN')
      setParentsOnly(existing.parents_only || false)

    }
  }, [eventId])

  // Gdy zmienia się start lub czas trwania, przelicz koniec (dla notatek i gdy nie cały dzień)
  useEffect(() => {
    if (type === 'note' && !allDay && startAt && durationMinutes) {
      const startDate = fromLocalInput(startAt)
      const newEnd = new Date(startDate.getTime() + durationMinutes * 60000)
      setEndAt(toLocalInput(newEnd))
    }
  }, [type, allDay, startAt, durationMinutes])

  // Kolor notatki wg domu autora (created_by dla edycji, zalogowany user dla nowych)
  const authorId = existing?.created_by || user?.id
  const authorMember = members.find(mb => mb.user_id === authorId)
  const authorHome = authorMember?.assigned_home || (authorMember?.role === 'parent_a' ? 'home_a' : 'home_b')
  const noteColor = authorHome === 'home_b' ? scheme.homeB.color : scheme.homeA.color
  const expenseColor = getTypeColor('expense', scheme.id)
  const modalBorderColor = type === 'note' ? noteColor : expenseColor

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user || !family || !childId) return
    // Blokuj dodawanie w planie Basic (edytowanie istniejących pozostaje dozwolone)
    if (!existing && !entitlements.canAddEvents) {
      return
    }
    setIsSaving(true)
    try {
      // Walidacja per typ
      if (type === 'expense') {
        if (amount === '' || Number(amount) <= 0) {
          setIsSaving(false)
          return
        }
      }
      if (type !== 'note') {
        // koszty: pojedyncza data, brak end_at, all_day=false
        const base = {
          family_id: family.id,
          child_id: childId,
          title: title.trim() || 'Wydatek',
          notes: notes?.trim() || null,
          start_at: fromLocalInput(startAt).toISOString() as any,
          end_at: null as any,
          all_day: false,
          location: location?.trim() || null,
          type: 'expense',
          amount: type === 'expense' ? Number(amount) : null,
          currency: type === 'expense' ? currency : 'PLN',
          metadata: {} as any,
          parents_only: parentsOnly,
        }
        if (existing) {
          // Aktualizacja: nie zmieniamy created_by – zachowujemy autora
          await updateEvent(existing.id, base as any)
        } else {
          await addEvent({ ...base, created_by: user.id } as any)
        }
        onClose()
        return
      }

      const baseNote = {
        family_id: family.id,
        child_id: childId,
        title: title.trim() || 'Notatka',
        notes: notes?.trim() || null,
        start_at: fromLocalInput(startAt).toISOString() as any,
        end_at: allDay ? null as any : fromLocalInput(endAt).toISOString() as any,
        all_day: allDay,
        location: location?.trim() || null,
        type: 'note' as const,
        amount: null as any,
        currency: 'PLN' as const,
        metadata: {} as any,
        parents_only: parentsOnly,
      }
      if (existing) {
        // Aktualizacja: nie nadpisuj created_by
        await updateEvent(existing.id, baseNote as any)
      } else {
        await addEvent({ ...baseNote, created_by: user.id } as any)
      }
      onClose()
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 p-2 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md border mt-[10vh] transition-colors duration-200" style={{ borderColor: modalBorderColor }}>
        {/* Top actions */}
        <div className="flex items-center justify-end px-4 pt-3">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm font-medium rounded-md text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors mr-2">Anuluj</button>
          <button type="submit" form="event-form" disabled={isSaving || (!existing && !entitlements.canAddEvents)} title={!existing && !entitlements.canAddEvents ? 'Dostępne w planach Pro/Max' : undefined} className="btn btn-sm btn-primary disabled:opacity-60">{isSaving ? 'Zapisywanie...' : (existing ? 'Zapisz' : 'Dodaj')}</button>
        </div>
        {/* Tabs full width */}
        <div className="px-4 pb-3 mt-3 border-b">
          <div className="grid grid-cols-2 gap-2">
            {(['note','expense'] as const).map(t => {
              const active = type === t
              const color = t === 'note' ? noteColor : expenseColor
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={`relative w-full py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${active ? 'text-white shadow-sm border-2' : 'border border-gray-200'}`}
                  style={{
                    backgroundColor: active ? color : `${color}15`,
                    borderColor: active ? color : undefined,
                    color: active ? undefined : color,
                  }}
                >
                  {t==='note' ? 'Notatka' : 'Koszt'}
                  {!entitlements.canAddEvents && (
                    <span className="absolute -top-2 -right-2 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500 text-white border border-amber-600">Pro/Max</span>
                  )}
                </button>
              )
            })}
          </div>
          {!existing && !entitlements.canAddEvents && (
            <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-200 text-amber-800 text-xs">
              Dodawanie wydarzeń, notatek i kosztów dostępne w planach Pro/Max. <a href="/pricing" className="underline">Zobacz Cennik</a>.
            </div>
          )}
        </div>

        <form id="event-form" onSubmit={handleSubmit} className="px-4 py-3">
          <div>
            <label className="block text-xs font-medium mb-1 text-slate-700">Tytuł</label>
            <input value={title} onChange={e => setTitle(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400" required />
          </div>

          <div className="min-h-[180px]">
            {type === 'note' ? (
              <>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <div>
                    <label className="block text-xs font-medium mb-1 text-slate-700">Od</label>
                    <input type="datetime-local" value={startAt} onChange={e => setStartAt(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs md:text-sm leading-tight" required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-slate-700">Czas trwania</label>
                    <select
                      value={durationMinutes}
                      onChange={e => setDurationMinutes(Number(e.target.value))}
                      className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      disabled={allDay}
                      required={!allDay}
                    >
                      {(NOTE_DURATION_OPTIONS.includes(durationMinutes) ? NOTE_DURATION_OPTIONS : [...NOTE_DURATION_OPTIONS, durationMinutes]).map(v => (
                        <option key={v} value={v}>{formatNoteDuration(v)}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <label htmlFor="allday" className="flex items-center gap-2.5 mt-3 py-1.5 cursor-pointer">
                  <input id="allday" type="checkbox" checked={allDay} onChange={e => setAllDay(e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-blue-500 focus:ring-blue-400" />
                  <span className="text-sm text-slate-700">Cały dzień</span>
                </label>
                <label htmlFor="parentsOnly" className="flex items-center gap-2.5 py-1.5 cursor-pointer">
                  <input id="parentsOnly" type="checkbox" checked={parentsOnly} onChange={e => setParentsOnly(e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-blue-500 focus:ring-blue-400" />
                  <span className="text-sm text-slate-700">Widoczne tylko dla rodziców</span>
                </label>
                <div className="mt-3">
                  <label className="block text-xs font-medium mb-1 text-slate-700">Miejsce</label>
                  <input value={location} onChange={e => setLocation(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400" />
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium mb-1 text-slate-700">Data</label>
                    <input type="datetime-local" value={startAt} onChange={e => setStartAt(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs md:text-sm leading-tight" required />
                  </div>
                  {type === 'expense' && (
                    <div>
                      <label className="block text-xs font-medium mb-1 text-slate-700">Kwota</label>
                      <div className="flex gap-2">
                        <input type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value === '' ? '' : Number(e.target.value))} className="w-full px-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400" required />
                        <select value={currency} onChange={e => setCurrency(e.target.value)} className="px-2 py-2 rounded-md border border-slate-300 focus:outline-none">
                          <option value="PLN">PLN</option>
                          <option value="EUR">EUR</option>
                          <option value="USD">USD</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
                <label htmlFor="parentsOnlyExp" className="flex items-center gap-2.5 mt-3 py-1.5 cursor-pointer">
                  <input id="parentsOnlyExp" type="checkbox" checked={parentsOnly} onChange={e => setParentsOnly(e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-blue-500 focus:ring-blue-400" />
                  <span className="text-sm text-slate-700">Widoczne tylko dla rodziców</span>
                </label>
              </>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium mb-1 text-slate-700">Notatki</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 min-h-[80px] focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
        </form>
      </div>
    </div>
  )
}

export default EventModal


